import { useState, useEffect, useContext } from 'react';
import { Modal, Form } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useAppDispatch, useAppSelector } from '../../app/hooks';

import {
  fetchDataQueries,
  ResolveSourceName,
  saveDataQuery,
} from '../../middleware/api/data_query_apis';
import '../../App.css';
import { useTranslation } from 'react-i18next';
import ToolButton, { ToolButtonType } from '../../tools/tool-button';
import { DataQuery } from '../../utils/types';

// types
import { Event, EventType, EventTarget } from '../../utils/events';

// types specifically for this component.
import { QueryParameters } from './search-catalog-types';
import { handleEvent } from '../../app-actions';

interface SaveQueryModalProps {
  show: boolean;
  handleClose: () => void;
  positionSearch: boolean;
  query: QueryParameters;
  project?: { id: number; name?: string };
  adqlQuery?: string;
}

const SaveQueryModal: React.FC<SaveQueryModalProps> = ({
  show,
  handleClose,
  positionSearch,
  query,
  project,
  adqlQuery,
}) => {
  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  // global state variables.
  const sG_darkMode: boolean = useAppSelector((state) => state.global.darkMode);

  // app variables.
  const sFontSize: number = useAppSelector((state) => state.app.fontSize);

  //	--------------------------------------------------------------------------
  //
  //	D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  // translation function
  const { i18n, t } = useTranslation();

  const dispatch = useAppDispatch();

  const [name, setName] = useState('');
  const [queryParam, setQueryParam] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // this state vairable will be used to resolve source name
  const [resolvedSourceName, setResolvedSourceName] = useState(false);

  useEffect(() => {
    if (!show) {
      setError('');
      setName('');
    }
  }, [show]);

  useEffect(() => {
    //
    if (adqlQuery) {
      //
      // For ADQL queries, use a different endpoint
      const encodedQuery = encodeURIComponent(adqlQuery);
      const newQueryParam = `/v1/data/execute_adql?query=${encodedQuery}`;
      setQueryParam(newQueryParam);
      //
    } else {
      //
      // For cone searches, use the existing endpoint
      setQueryParam(
        buildQuery({
          query: query,
        }),
      );
      //
    }
    //
  }, [query, resolvedSourceName, adqlQuery]);

  //	------------------------------------------------------------
  //
  //	builds a query string from the supplied parameters.
  //
  //	------------------------------------------------------------

  function buildQuery(args: { query: QueryParameters }) {
    //
    const params = new URLSearchParams();
    if (
      args.query.ra !== undefined &&
      args.query.dec !== undefined &&
      args.query.fov !== undefined
    ) {
      params.append('ra', args.query.ra.toString());
      params.append('dec', args.query.dec.toString());
      params.append('radius', args.query.fov.toString());
    } else {
      if (args.query.sourceName !== undefined) {
        params.append('source_name', args.query.sourceName ?? '');
      }
    }
    if (args.query.dataProductType !== undefined) {
      params.append('dataproduct_type', args.query.dataProductType);
    }
    if (args.query.namespace !== undefined) {
      params.append('namespace', args.query.namespace);
    }
    if (args.query.filename !== undefined) {
      params.append('filename', args.query.filename);
    }
    if (args.query.collection !== undefined) {
      params.append('collection', args.query.collection);
    }
    if (args.query.facility !== undefined) {
      params.append('facility', args.query.facility);
    }
    if (args.query.instrument !== undefined) {
      params.append('instrument', args.query.instrument);
    }
    const paramString: string = params.toString();
    var query: string = '/v1/data/get_data';
    if (paramString.length > 0) {
      query = query + '?' + paramString;
    }

    return query;
    //
  } // buildQuery

  // TODO: During innovation sprint
  //const saveQueryMutation = useSaveQuery();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    if (e.target.value.trim() !== '') {
      setError('');
    }
  };

  const handleSave = async () => {
    //
    setError('');
    if (!name.trim()) {
      setError('Please enter a name for the query.');
      return;
    }

    setLoading(true);

    try {
      //
      const response = await saveDataQuery({
        project_id: project?.id,
        name: name,
        query_param: queryParam,
      });

      // raise an event to handle the refresh of the queries.
      const newEvent: Event = {
        eventTarget: EventTarget.GLOBAL,
        eventType: EventType.DATA_QUERY_ADDED,
        parameters: project,
      };
      dispatch(
        handleEvent({
          event: newEvent,
        }),
      );

      handleClose();
      //
    } catch (error: any) {
      setError(error.message || 'Failed to save query');
      setLoading(false);
    } finally {
      setLoading(false);
    }
    //
  };

  const modalStyle = {
    color: sG_darkMode ? '#fff' : '#000',
    borderRadius: '0px',
    margin: '0px',
    border: 'none',
  };

  const toTitleCase = (str: string | undefined) => {
    return str?.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <div
        className="panel-background"
        style={{
          ...modalStyle,
          ...{
            fontSize: sFontSize.toString() + 'pt',
          },
        }}
        data-theme={sG_darkMode ? 'dark' : 'light'}
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1.1em', fontWeight: '800' }}>
            Save Data Query
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ borderRadius: '0px' }}>
          <Form>
            <Form.Group controlId="queryName">
              <label
                id="save-query-name-label"
                style={{
                  margin: '0px 0px 5px 0px',
                }}
              >
                Query name:
              </label>
              <input
                aria-labelledby="save-query-name-label"
                className="input-field"
                style={{
                  marginBottom: '10px',
                }}
                type="text"
                placeholder={t('Enter query name')}
                value={name}
                autoComplete="off"
                maxLength={20}
                onChange={handleInputChange}
              />
              {error && (
                <div style={{ color: 'red', margin: '5px 0px 10px 0px' }}>{error}</div>
              )}
            </Form.Group>

            <Form.Group
              controlId="queryParam"
              style={{ height: 'auto', display: 'flex', flexDirection: 'column' }}
            >
              <label
                id="save-query-details-label"
                style={{
                  margin: '0px 0px 5px 0px',
                }}
              >
                Query details:
              </label>
              <textarea
                area-labelledby="save-query-details-label"
                rows={10}
                className="input-field"
                style={{
                  marginBottom: '20px',
                  padding: '5px',
                  height: '200px',
                }}
                data-disabled="T"
                value={queryParam}
                readOnly
                dir="ltr"
              />
            </Form.Group>

            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-end',
              }}
            >
              <ToolButton
                key={t('Close')}
                name="close"
                onClick={handleClose}
                tooltip="Close save query modal"
                text={t('Close')}
                type={ToolButtonType.SECONDARY}
              />
              <div style={{ padding: '5px' }} />
              <ToolButton
                key={t('Save')}
                name="save"
                onClick={handleSave}
                disabled={loading}
                tooltip="Save query"
                text={loading ? t('Saving...') : t('Save')}
                type={ToolButtonType.PRIMARY}
              />
            </div>
          </Form>
        </Modal.Body>
      </div>
    </Modal>
  );
};

export default SaveQueryModal;
